import { useI18nContext } from '@lemonade-hq/nuiza';
import { getRandomId } from '@lemonade-hq/ts-helpers';
import type { Seconds } from '@lemonade-hq/ts-helpers';
import { interpolate, isTextProps } from '@lemonade-hq/uniclient-chat';
import type {
  PriceProps as BasePriceProps,
  Benefit,
  FormattedText,
  IconTextRowProps,
  UniclientAction,
} from '@lemonade-hq/uniclient-chat';
import {
  Button,
  buttonLayoutTransition,
  circle,
  Flex,
  Icon,
  IconButton,
  Odometer,
  Separator,
  spacing,
  Text,
  themeVars,
  Tooltip,
  useViewportSize,
} from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import React, { lazy, Suspense, useMemo } from 'react';
import type { OnActionFn } from '../../../elements/types';
import { WizardFormattedText } from '../../../elements/WizardFormattedText';
import { DynamicComponent } from '../../DynamicComponent';
import type { UniclientComponentAdapterProps } from '../../types';
import { WizardComponentAdapter } from '../../WizardComponentAdapter';
import { IconTextList } from '../IconTextList/IconTextList';
import * as styles from './Price.css';

const MAX_VISIBLE_ITEMS_DEFAULT = 6;
const MAX_VISIBLE_ITEMS_TINY = 10;
const BUTTON_ANIMATION_DELAY: Seconds = 0.3;
const BUTTON_ANIMATION_DURATION: Seconds = 0.4;

const defaultAnswers = {};

export type PriceVariant = 'default' | 'tiny';

type PriceAdditionalComponentProps = BasePriceProps['additionalComponent'] &
  Omit<UniclientComponentAdapterProps, 'answers' | 'componentProps'>;

export interface PriceProps extends Omit<BasePriceProps, 'additionalComponent'> {
  readonly variant?: PriceVariant;
  readonly showMoreText?: string;
  readonly priceClassName?: string;
  readonly action?: UniclientAction;
  readonly showButtonNextToPrice?: boolean;
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly onAction?: OnActionFn;
  readonly additionalComponent?: PriceAdditionalComponentProps;
  readonly buttonSize?: 'sm' | 'xs';
  readonly answers?: UniclientComponentAdapterProps['answers'];
  readonly hideBenefits?: boolean;
}

const Diamond = lazy(async () => await import('../Diamond/Diamond').then(module => ({ default: module.Diamond })));

export const Price: React.FC<PriceProps> = ({
  answers,
  additionalComponent,
  price,
  moreInfo,
  benefits = [],
  discount,
  initialVisibleBenefits = benefits.length,
  period,
  previousPrice,
  variant = 'default',
  showMoreText,
  title,
  priceClassName,
  action,
  showButtonNextToPrice = false,
  loading,
  disabled = false,
  onAction,
  buttonSize,
  disclaimer,
  withDiamond = true,
  listColorVariant,
  disableMaxVisibleBenefits = false,
  hideBenefits = false,
}) => {
  const { s } = useI18nContext();
  const [showMore, setShowMore] = React.useState(false);
  const hasAction = action?.content != null && onAction != null;
  const hasMoreInfoAction = moreInfo?.action != null && onAction != null;

  const maxVisibleBenefits = variant === 'default' ? MAX_VISIBLE_ITEMS_DEFAULT : MAX_VISIBLE_ITEMS_TINY;

  const adjustedMaxVisibleItems = disableMaxVisibleBenefits
    ? benefits.length
    : Math.min(
        initialVisibleBenefits,
        initialVisibleBenefits > maxVisibleBenefits ? maxVisibleBenefits - 1 : maxVisibleBenefits,
      );

  const { visibleBenefits, collapsibleBenefits } = useMemo(() => {
    const mapBenefit = (benefit: Benefit | string): IconTextRowProps => {
      if (typeof benefit === 'string') {
        return { text: benefit, textColorVariant: listColorVariant };
      }

      const { icon, text } = benefit;
      const iconName = typeof icon === 'string' ? icon : icon.name;
      const iconVariant = typeof icon === 'object' ? icon.variant : undefined;

      return { icon: iconName, text, variant: iconVariant, textColorVariant: listColorVariant };
    };

    return {
      visibleBenefits: benefits.slice(0, adjustedMaxVisibleItems).map(mapBenefit),
      collapsibleBenefits: benefits.slice(adjustedMaxVisibleItems).map(mapBenefit),
    };
  }, [benefits, adjustedMaxVisibleItems, listColorVariant]);

  const showMoreTextFallback = `+${collapsibleBenefits.length} ${s('chat.pricing.read_more')}`;
  const isShowMoreTextVisible = !showMore && benefits.length > adjustedMaxVisibleItems;
  const discountLabel = discount !== undefined ? discount.label : null;
  const discountDetails = discount !== undefined ? discount.details : null;
  const uniqueId = useMemo(() => getRandomId(), []);
  const { isBelowDesktopViewport } = useViewportSize();
  const largeVersion = variant === 'default' && !isBelowDesktopViewport;
  const priceText = largeVersion ? 'h2' : 'h4';
  const listSize = variant === 'default' ? 'md' : 'sm';
  const currentAnswers = answers ?? defaultAnswers;
  const priceInterpolated = interpolate(price, currentAnswers);

  const previousPriceInterpolated = useMemo(() => {
    if (previousPrice == null) return;
    if (typeof previousPrice === 'string') {
      return interpolate(previousPrice, currentAnswers);
    }

    if (Array.isArray(previousPrice)) {
      return previousPrice.map(item => {
        if (isTextProps(item)) {
          return { ...item, text: interpolate(item.text, currentAnswers) };
        }

        return item;
      }) as FormattedText;
    }

    if (isTextProps(previousPrice)) {
      return { ...previousPrice, text: interpolate(previousPrice.text, currentAnswers) };
    }

    return previousPrice;
  }, [previousPrice, currentAnswers]);

  const mainActionButton = hasAction ? (
    <Button
      aria-label="payment button"
      disabled={disabled || action.disabled}
      ellipsis={false}
      layoutId="price-button"
      loading={loading}
      onClick={() => void onAction(action)}
      size={buttonSize}
      transition={{
        layout: {
          ...buttonLayoutTransition,
          delay: BUTTON_ANIMATION_DELAY,
          duration: BUTTON_ANIMATION_DURATION,
        },
      }}
      type="button"
      variant="primary"
    >
      <motion.div
        layout="position"
        layoutId="price-button-content"
        style={{ width: '100%' }}
        transition={{
          layout: {
            ...buttonLayoutTransition,
            delay: BUTTON_ANIMATION_DELAY,
            duration: BUTTON_ANIMATION_DURATION,
          },
        }}
      >
        <Text type="h6">{action.content}</Text>
      </motion.div>
    </Button>
  ) : null;

  return (
    <>
      <WizardComponentAdapter
        display="flex"
        flexDirection="column"
        gap={{ sm: spacing.s16, lg: variant === 'default' ? spacing.s08 : spacing.s12 }}
        type="price"
        width="100%"
      >
        <Flex alignItems="center" flexDirection="row" gap={spacing.s08} justifyContent="space-between">
          <Flex flexDirection="column" justifyContent="flex-start">
            {title != null && (
              <Text color="$textSecondary" type="bodyMedium">
                {title}
              </Text>
            )}
            <Flex flexDirection="column">
              {previousPriceInterpolated != null && (
                <WizardFormattedText aria-label="previous price" text={previousPriceInterpolated} type="bodyMedium" />
              )}
              <Flex alignItems="center" aria-label="current price" gap={spacing.s04}>
                <Flex alignItems="baseline" marginBlock={{ sm: 0, lg: variant === 'default' ? spacing.s06 : 0 }}>
                  <Odometer
                    aria-label="price value"
                    text={`${priceInterpolated}/`}
                    textClassName={priceClassName}
                    textType={priceText}
                  />

                  <motion.div layoutId={`${uniqueId}-period`}>
                    <Text type="bodyMedium">{period}</Text>
                  </motion.div>
                </Flex>
                {withDiamond && (
                  <motion.div layoutId={`${uniqueId}-diamond`}>
                    <Suspense fallback={null}>
                      <Diamond width={variant === 'default' && largeVersion ? '31px' : '24px'} />
                    </Suspense>
                  </motion.div>
                )}
              </Flex>
              {discountLabel != null &&
                (discountDetails != null ? (
                  <Tooltip aria-label="discount description" content={discountDetails}>
                    <Text color="$textSecondary" cursor="default" type="bodyMedium">
                      {discountLabel}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text aria-label="discount" color="$textSecondary" type="bodyMedium">
                    {discountLabel}
                  </Text>
                ))}
            </Flex>
          </Flex>
          {showButtonNextToPrice && mainActionButton}
        </Flex>
        {additionalComponent != null && (
          <Flex flexDirection="column" gap={spacing.s12} pb={spacing.s08}>
            <Text aria-label="additional price description" color="$textSecondary" type="bodyMedium">
              {additionalComponent.description}
            </Text>
            <DynamicComponent
              {...additionalComponent}
              answers={currentAnswers}
              component={additionalComponent.component}
              onAction={onAction}
            />
          </Flex>
        )}
        {variant === 'default' && <Separator className={styles.separator} />}
        {moreInfo != null && (
          <Flex alignItems="center" aria-label="additional details" justifyContent="space-between">
            <Flex alignItems="center" gap={spacing.s08} paddingBlockEnd={spacing.s04}>
              {moreInfo.icon != null && <Icon name={moreInfo.icon} size="md" />}
              <Text color="$textSecondary" type="bodyMedium">
                {moreInfo.text}
              </Text>
            </Flex>
            {hasMoreInfoAction && (
              <IconButton
                disabled={disabled || moreInfo.action.disabled}
                icon={moreInfo.action.icon ?? 'edit'}
                id="edit"
                onClick={() => void onAction(moreInfo.action)}
                size="sm"
              />
            )}
          </Flex>
        )}
        <AnimatePresence>
          {!hideBenefits && (
            <Flex
              alignItems="flex-start"
              animate={{ opacity: 1 }}
              aria-label="benefits"
              as={motion.div}
              exit={{ opacity: 0 }}
              flexDirection="column"
              gap={spacing.s04}
              initial={{ opacity: 0 }}
              key="benefits"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <IconTextList items={visibleBenefits} size={listSize} withAnimation={false} />
              <AnimatePresence>
                {isShowMoreTextVisible && (
                  <motion.button
                    className={styles.readMore}
                    exit={{ opacity: 0, height: 0, marginBottom: `-${spacing.s04}` }}
                    initial={{ opacity: 1, height: 'auto', marginBottom: 0 }}
                    key="read-more"
                    onClick={() => setShowMore(true)}
                    type="button"
                  >
                    <Flex alignItems="center" gap={spacing.s08} m={spacing.s04}>
                      <Text color={themeVars.color.textPrimary} type="bodyMedium">
                        {showMoreText ?? showMoreTextFallback}
                      </Text>
                      <Flex
                        alignItems="center"
                        backgroundColor="$backgroundTertiary"
                        className={circle}
                        height={spacing.s16}
                        justifyContent="center"
                      >
                        <Icon name="dropdown" size="sm" variant="secondary" />
                      </Flex>
                    </Flex>
                  </motion.button>
                )}
                {showMore && <IconTextList items={collapsibleBenefits} size={listSize} />}
              </AnimatePresence>
            </Flex>
          )}
        </AnimatePresence>
        {disclaimer != null && (
          <Text color="$textSecondary" type="bodyMedium">
            {disclaimer}
          </Text>
        )}
      </WizardComponentAdapter>
      {!showButtonNextToPrice && mainActionButton}
    </>
  );
};
